import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Radio, Loader2, Wifi, Square, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
  adminStartLive,
  adminEndLive,
  adminFetchActiveLive,
} from "../../lib/studioApi";
import { formatDistanceToNow } from "date-fns";

const GLOBAL_ID = "__global__";
const QUICK_MEET_URL = "https://meet.new";

function safeAgo(iso) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
}

/**
 * GoLiveButton — admin-only CTA. Shows current live state (start / end) and
 * opens a dialog to start a session with title + meet URL + scope.
 * Polls /admin/live/active every 15s to keep the local state honest.
 */
export default function GoLiveButton({ users = [] }) {
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [meetUrl, setMeetUrl] = useState("");
  const [scope, setScope] = useState("global");
  const [targetUserId, setTargetUserId] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    adminFetchActiveLive()
      .then(setActive)
      .catch(() => {});
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, []);

  // Lazy default — when the dialog opens, if no userId selected yet, pick first user
  useEffect(() => {
    if (open && scope === "user" && !targetUserId && users.length > 0) {
      setTargetUserId(users[0].id);
    }
  }, [open, scope, targetUserId, users]);

  const handleStart = async () => {
    if (!title.trim()) {
      toast.error("Add a session title");
      return;
    }
    if (!meetUrl.trim() || !/^https?:\/\//i.test(meetUrl.trim())) {
      toast.error("Meet URL must start with http(s)://");
      return;
    }
    if (scope === "user" && !targetUserId) {
      toast.error("Pick an artist to invite");
      return;
    }
    setBusy(true);
    try {
      const session = await adminStartLive({
        title: title.trim(),
        meetUrl: meetUrl.trim(),
        scope,
        userId: scope === "user" ? targetUserId : null,
        sendEmail,
      });
      setActive(session);
      setOpen(false);
      setTitle("");
      setMeetUrl("");
      setSendEmail(false);
      toast.success(
        sendEmail && session.emailedCount
          ? `Live! Invited ${session.emailedCount} artist${session.emailedCount === 1 ? "" : "s"} via email.`
          : "You're live!",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start live session");
    } finally {
      setBusy(false);
    }
  };

  const handleEnd = async () => {
    if (!active?.id) return;
    if (!window.confirm("End this live session?")) return;
    setBusy(true);
    try {
      await adminEndLive(active.id);
      setActive(null);
      toast.success("Live session ended");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not end session");
    } finally {
      setBusy(false);
    }
  };

  if (active) {
    return (
      <div
        data-testid="admin-live-active-card"
        className="rounded-xl border border-red-500/30 bg-gradient-to-r from-red-600/15 via-red-600/10 to-red-500/15 p-4 mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
      >
        <div className="flex items-start gap-3">
          <div className="relative mt-1 flex-shrink-0">
            <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-60" />
            <span className="relative block w-3 h-3 rounded-full bg-red-500" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold bg-red-600 text-white px-2 py-0.5 rounded">
                Live now
              </span>
              <span className="text-xs text-red-200/80">
                {active.scope === "global" ? "Everyone" : "1-on-1"} ·
                {" "}
                {safeAgo(active.startedAt)}
              </span>
            </div>
            <div
              data-testid="admin-live-active-title"
              className="font-bold text-white text-sm md:text-base mt-0.5"
            >
              {active.title}
            </div>
            <a
              href={active.meetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200 underline mt-0.5 break-all"
            >
              <ExternalLink className="w-3 h-3" />
              {active.meetUrl}
            </a>
          </div>
        </div>
        <Button
          data-testid="admin-end-live-btn"
          onClick={handleEnd}
          disabled={busy}
          variant="outline"
          className="border-red-400/40 text-red-200 bg-transparent hover:bg-red-500/15 hover:text-white gap-2 self-start md:self-auto"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Square className="w-4 h-4" />
          )}
          End live
        </Button>
      </div>
    );
  }

  return (
    <>
      <Button
        data-testid="admin-go-live-btn"
        onClick={() => setOpen(true)}
        className="bg-red-600 hover:bg-red-700 text-white gap-2 mb-4 font-bold"
      >
        <Radio className="w-4 h-4" />
        Go Live
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="go-live-dialog" className="max-w-md bg-[#10162a] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Wifi className="w-5 h-5 text-red-400" />
              Start a live session
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                Title / topic
              </Label>
              <Input
                data-testid="go-live-title-input"
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Mix review for Track 1"
                className="mt-1 bg-[#0b0f1e] border-white/10 text-white placeholder:text-white/30 focus-visible:border-paul-yellow/60 focus-visible:ring-paul-yellow/40"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                Google Meet URL
              </Label>
              <Input
                data-testid="go-live-meet-input"
                value={meetUrl}
                onChange={(e) => setMeetUrl(e.target.value)}
                placeholder="https://meet.google.com/abc-defg-hij"
                className="mt-1 bg-[#0b0f1e] border-white/10 text-white placeholder:text-white/30 focus-visible:border-paul-yellow/60 focus-visible:ring-paul-yellow/40"
              />
              <button
                type="button"
                data-testid="go-live-quick-meet"
                onClick={() => window.open(QUICK_MEET_URL, "_blank", "noopener,noreferrer")}
                className="text-[11px] text-white/50 hover:text-paul-yellow mt-1.5 inline-flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                Need a fresh link? Open <span className="font-mono">meet.new</span> in a new tab
              </button>
            </div>

            <div>
              <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                Who should see it?
              </Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger
                  data-testid="go-live-scope-select"
                  className="mt-1 bg-[#0b0f1e] border-white/10 text-white focus:ring-paul-yellow/40"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">📢 Everyone — all active artists</SelectItem>
                  <SelectItem value="user">👤 Specific artist (1-on-1)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {scope === "user" && (
              <div>
                <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                  Pick artist
                </Label>
                <Select value={targetUserId} onValueChange={setTargetUserId}>
                  <SelectTrigger
                    data-testid="go-live-user-select"
                    className="mt-1 bg-[#0b0f1e] border-white/10 text-white focus:ring-paul-yellow/40"
                  >
                    <SelectValue placeholder="Select an artist" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.artistName || u.name} — {u.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <label
              data-testid="go-live-email-toggle"
              className="flex items-start gap-2.5 cursor-pointer select-none"
            >
              <Checkbox
                checked={sendEmail}
                onCheckedChange={(v) => setSendEmail(Boolean(v))}
                className="mt-0.5 border-white/30 data-[state=checked]:bg-paul-yellow data-[state=checked]:text-navy data-[state=checked]:border-paul-yellow"
              />
              <span className="text-sm text-white/80">
                Also email everyone the join link
                <span className="block text-[11px] text-white/40 font-normal">
                  Sends a Resend email blast with a red CTA button. Off by default.
                </span>
              </span>
            </label>
          </div>

          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={busy}
              className="border-white/15 bg-transparent text-white/80 hover:bg-white/10 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              type="button"
              data-testid="go-live-start-btn"
              onClick={handleStart}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700 text-white gap-2 font-bold"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />}
              Go live
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
